import { AuthProvider } from "react-admin";
import { OktaAuth } from "@okta/okta-auth-js";

const OKTA_ISSUER = process.env.REACT_APP_OKTA_ISSUER || "";
const CLIENT_ID = process.env.REACT_APP_OKTA_CLIENT_ID || "";
const REDIRECT_URI = `${window.location.origin}/auth-callback`;
const OKTA_LOCAL_STORAGE_KEY =
  process.env.REACT_APP_OKTA_LOCAL_STORAGE_KEY || "okta-token-storage";
// Role defined in ENV for dev mode
const DEV_ROLE = process.env.REACT_APP_ROLE;

const oktaAuth = new OktaAuth({
  issuer: OKTA_ISSUER,
  clientId: CLIENT_ID,
  redirectUri: REDIRECT_URI,
  scopes: ["openid", "profile", "email", "groups"],
  pkce: true,
});

const getUserDetails = () => {
  const user = localStorage.getItem(OKTA_LOCAL_STORAGE_KEY);
  if (user) {
    const parsedUser = JSON.parse(user);
    return {
      id: parsedUser.idToken.claims.sub,
      fullName: parsedUser.idToken.claims.name,
      email: parsedUser.idToken.claims.email,
      roles: (() => {
        const groups = parsedUser.accessToken.claims.groups;
        if (!groups || !Array.isArray(groups)) return undefined;
        const roles = [];
        if (groups.includes("App - ATLAS - Admin") || DEV_ROLE === "admin")
          roles.push("admin");
        if (
          groups.includes("App - ATLAS - Admin [CVE]") ||
          DEV_ROLE === "cve_admin"
        )
          roles.push("cve_admin");
        if (
          groups.includes("App - ATLAS - Editor [CVE]") ||
          groups.includes("App - ATLAS - Editor") ||
          DEV_ROLE === "cve_editor"
        )
          roles.push("cve_editor");
        if (
          groups.includes("App - ATLAS - Viewer [CVE]") ||
          groups.includes("App - ATLAS - Viewer") ||
          DEV_ROLE === "cve_viewer"
        )
          roles.push("cve_viewer");
        if (
          groups.includes("App - ATLAS - Admin [RMF]") ||
          DEV_ROLE === "rmf_admin"
        )
          roles.push("rmf_admin");
        if (
          groups.includes("App - ATLAS - Editor [RMF]") ||
          DEV_ROLE === "rmf_editor"
        )
          roles.push("rmf_editor");
        if (
          groups.includes("App - ATLAS - Auditor [RMF]") ||
          DEV_ROLE === "rmf_auditor"
        )
          roles.push("rmf_auditor");
        if (
          groups.includes("App - ATLAS - Viewer [RMF]") ||
          DEV_ROLE === "rmf_viewer"
        )
          roles.push("rmf_viewer");
        return roles.length > 0 ? roles : undefined;
      })(),
    };
  } else {
    return null;
  }
};

export const authProvider: AuthProvider = {
  login: async () => {
    if (DEV_ROLE) {
      localStorage.setItem(
        OKTA_LOCAL_STORAGE_KEY,
        JSON.stringify({
          idToken: {
            claims: {
              sub: "dev",
              name: "Dev User",
              email: "dev@astronomer.io",
            },
          },
          accessToken: {
            claims: {
              groups: [DEV_ROLE],
            },
          },
        })
      );
      // redirect to home page
      window.location.replace("/");
      return Promise.resolve();
    }
    await oktaAuth.token.getWithRedirect();
  },
  logout: async () => {
    try {
      await oktaAuth.revokeAccessToken();
      await oktaAuth.revokeRefreshToken();
      await oktaAuth.closeSession();
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem(OKTA_LOCAL_STORAGE_KEY);
  },
  checkAuth: async () => {
    const user = getUserDetails();
    if (user) {
      let { accessToken } = await oktaAuth.tokenManager.getTokens();
      const now = Date.now() / 1000;
      // If token is expired or about to expire in the next 60 seconds
      if (
        accessToken &&
        accessToken.expiresAt &&
        accessToken.expiresAt < now + 60
      ) {
        try {
          const renewedToken = await oktaAuth.tokenManager.renew("accessToken");
          // Type guard: check if renewedToken is an AccessToken
          if (
            renewedToken &&
            "accessToken" in renewedToken &&
            "expiresAt" in renewedToken
          ) {
            accessToken = renewedToken;
          } else {
            // If we didn't get a valid access token, treat as failure
            return Promise.reject();
          }
        } catch (error) {
          // Renewal failed, probably session expired
          return Promise.reject();
        }
      }
      // After renewal attempt, check again
      if (accessToken && accessToken.expiresAt && accessToken.expiresAt < now) {
        return Promise.reject();
      }
      return user ? Promise.resolve() : Promise.reject();
    }
    return Promise.reject();
  },
  checkError: async (error) => {
    if (error && error.status === 401) {
      return Promise.reject();
    }
    return Promise.resolve();
  },
  getPermissions: async () => {
    const user = getUserDetails();
    return user?.roles ? Promise.resolve(user.roles) : Promise.reject();
  },
  getIdentity: async () => {
    const user = getUserDetails();
    return user ? Promise.resolve(user) : Promise.reject();
  },
  handleCallback: async () => {
    const tokens = await oktaAuth.token.parseFromUrl();
    oktaAuth.tokenManager.setTokens(tokens.tokens);
    window.location.replace("/");
  },
  canAccess: async ({ resource, action }) => {
    const user = getUserDetails();
    const roles = user?.roles || [];
    // Full permissions
    if (roles.includes("admin")) {
      return true;
    }

    if (resource.includes("cve")) {
      // all allowed by cve admin except delete
      if (action !== "delete" && roles.includes("cve_admin")) {
        return true;
      }
      // List and show are allowed for all users
      if (
        (action === "list" || action === "show") &&
        (roles.includes("cve_editor") || roles.includes("cve_viewer"))
      ) {
        return true;
      }
      if (
        [
          "cve/image-sources",
          "cve/code-sources",
          "cve/tracked-cves",
          "cve/sboms",
          "cve/pre-releases",
        ].includes(resource) &&
        (action === "create" || action === "edit") &&
        roles.includes("cve_editor")
      ) {
        return true;
      }
      // Delete is allowed for editors on sboms and pre-releases only
      if (
        ["cve/sboms", "cve/pre-releases"].includes(resource) &&
        action === "delete" &&
        roles.includes("cve_editor")
      ) {
        return true;
      }
    } else if (resource.includes("rmf")) {
      // all allowed by rmf admin except delete
      if (action !== "delete" && roles.includes("rmf_admin")) {
        return true;
      }
      // List and show for risks are allowed for all users (filtered downstream)
      if (
        (action === "list" || action === "show") &&
        resource === "rmf/risks" &&
        (roles.includes("rmf_editor") ||
          roles.includes("rmf_viewer") ||
          roles.includes("rmf_auditor"))
      ) {
        return true;
      }
      // Create allowed for editors on risk tickets only
      if (
        resource === "rmf/risks" &&
        action === "create" &&
        roles.includes("rmf_editor")
      ) {
        return true;
      }
    }
    return false;
  },
};
